export const ENTRY_LOG_INITIAL_VISIBLE = 6;
export const ENTRY_LOG_PAGE_SIZE = 10;

export type EntryLogPagination = {
  rangeStart: number;
  rangeEnd: number;
  shownCount: number;
  loadedCount: number;
  totalCount: number;
  hiddenLoadedCount: number;
  hasMoreOnServer: boolean;
  canShowMore: boolean;
  canShowAllLoaded: boolean;
  canCollapse: boolean;
  nextStepCount: number;
  remainingTotalCount: number;
  progressPercent: number;
};

export function computeEntryLogPagination(
  visibleCount: number,
  loadedCount: number,
  totalCount: number,
  olderCursor: string | null,
): EntryLogPagination {
  const effectiveTotal = Math.max(totalCount, loadedCount);
  const shownCount = Math.min(visibleCount, loadedCount, effectiveTotal || loadedCount);
  const rangeStart = shownCount > 0 ? 1 : 0;
  const rangeEnd = shownCount;
  const hiddenLoadedCount = Math.max(0, loadedCount - shownCount);
  const hasMoreOnServer = shownCount >= loadedCount && Boolean(olderCursor);
  const canShowMore = hiddenLoadedCount > 0 || hasMoreOnServer;
  const nextStepCount =
    hiddenLoadedCount > 0 ? Math.min(ENTRY_LOG_PAGE_SIZE, hiddenLoadedCount) : ENTRY_LOG_PAGE_SIZE;
  const canShowAllLoaded = hiddenLoadedCount > ENTRY_LOG_PAGE_SIZE;
  const canCollapse = shownCount > ENTRY_LOG_INITIAL_VISIBLE;
  const remainingTotalCount = Math.max(0, effectiveTotal - shownCount);
  const progressPercent =
    effectiveTotal > 0 ? Math.min(100, Math.round((shownCount / effectiveTotal) * 100)) : 100;

  return {
    rangeStart,
    rangeEnd,
    shownCount,
    loadedCount,
    totalCount: effectiveTotal,
    hiddenLoadedCount,
    hasMoreOnServer,
    canShowMore,
    canShowAllLoaded,
    canCollapse,
    nextStepCount,
    remainingTotalCount,
    progressPercent,
  };
}

export function formatEntryLogRange(pagination: EntryLogPagination): string {
  const { rangeStart, rangeEnd, totalCount } = pagination;
  if (totalCount === 0 || rangeEnd === 0) {
    return "No entries";
  }
  const noun = totalCount === 1 ? "entry" : "entries";
  if (rangeStart === rangeEnd && rangeEnd === totalCount) {
    return `Showing ${rangeStart} to ${rangeEnd} (${totalCount} ${noun})`;
  }
  return `Showing ${rangeStart} to ${rangeEnd} of ${totalCount} ${noun}`;
}

export function showMoreLabel(pagination: EntryLogPagination): string {
  if (pagination.hiddenLoadedCount > 0) {
    const n = pagination.nextStepCount;
    return `Show ${n} more`;
  }
  return `Load ${pagination.nextStepCount} older`;
}
